import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { AVATARS } from '@/data/mockData';
import { ScrollArea } from '@/components/ui/scroll-area';
import MediaArchive, { collectProjectFiles } from '@/components/shared/MediaArchive';
import { ArrowLeft, Zap, Settings, Check, LogOut, Camera } from 'lucide-react';

export default function UserPanel({ onClose, onShowSettings, onShowCredits, autoEdit = false }) {
  const { projects, isLoggedIn } = useApp();
  const { user, profile, signOut, updateProfile } = useAuth();
  const navigate = useNavigate();

  // Real or mock identity
  const displayName = isLoggedIn
    ? (profile?.full_name?.trim() || user?.email?.split('@')[0] || 'Set up profile')
    : 'Carlos Mendoza';
  const displayEmail = isLoggedIn ? user?.email : 'carlos@example.com';
  const displayAvatar = isLoggedIn ? (profile?.avatar_url || null) : AVATARS[0];
  const credits = isLoggedIn ? (profile?.credits ?? 0) : 12;
  const profileIncomplete = isLoggedIn && !profile?.full_name?.trim();

  // Profile editor (inline)
  const [editing, setEditing] = useState(profileIncomplete || autoEdit);
  const [draftName, setDraftName] = useState(profile?.full_name || '');
  const [draftAvatar, setDraftAvatar] = useState(profile?.avatar_url || '');
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  useEffect(() => {
    setDraftName(profile?.full_name || '');
    setDraftAvatar(profile?.avatar_url || '');
  }, [profile?.full_name, profile?.avatar_url]);

  // Auto-open editor when triggered from Settings → Edit profile
  useEffect(() => {
    if (autoEdit) setEditing(true);
  }, [autoEdit]);

  const handleSaveProfile = async () => {
    setSaving(true);
    const { error } = await updateProfile({
      full_name: draftName.trim(),
      avatar_url: draftAvatar.trim() || null,
    });
    setSaving(false);
    if (!error) {
      setSavedTick(true);
      setTimeout(() => { setSavedTick(false); setEditing(false); }, 900);
    }
  };

  const handleAvatarFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setDraftAvatar(reader.result);
    reader.readAsDataURL(file);
  };

  const allFiles = projects.flatMap(p => collectProjectFiles(p));

  return (
    <div className="flex flex-col h-[100dvh] md:h-full relative" data-testid="user-panel">
      <div className="absolute top-0 left-0 right-0 z-10 px-4 md:px-6 pt-4 pointer-events-none">
        <div className="flex justify-center pl-12 md:pl-0 pointer-events-auto">
          <div className="glass-pill flex items-center gap-2.5 px-2 py-1.5 rounded-full">
            <button data-testid="user-panel-back" onClick={onClose} className="p-1.5 rounded-full hover:bg-black/[0.04] transition-colors">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="text-[13px] font-medium text-gray-900 font-['Outfit'] pr-3">Cuenta</span>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="max-w-lg mx-auto w-full px-4 sm:px-6 pt-20 pb-8 space-y-6">
          {/* User info */}
          <section data-testid="account-section">
            {!editing ? (
              <div className="flex items-center gap-4">
                {displayAvatar ? (
                  <img src={displayAvatar} alt="Profile" className="w-14 h-14 rounded-full object-cover bg-gray-100 shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center shrink-0 text-base font-semibold text-gray-500">
                    {(displayName || '?').slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-medium text-gray-900 font-['Outfit'] truncate">{displayName}</h3>
                  <p className="text-sm text-gray-400 truncate">{displayEmail}</p>
                </div>
                {isLoggedIn && (
                  <button
                    data-testid="edit-profile-btn"
                    onClick={() => setEditing(true)}
                    className="text-[11px] text-gray-400 hover:text-gray-700 transition-colors px-2.5 py-1 rounded-md"
                  >
                    Edit
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {profileIncomplete && (
                  <p className="text-xs text-amber-600">Completa tu perfil para una mejor experiencia.</p>
                )}
                <div className="flex items-center gap-4">
                  <label className="relative cursor-pointer shrink-0" data-testid="profile-avatar-label">
                    {draftAvatar ? (
                      <img src={draftAvatar} alt="" className="w-14 h-14 rounded-full object-cover bg-gray-100" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-base font-semibold text-gray-500">
                        {(draftName || displayEmail || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-gray-900 flex items-center justify-center">
                      <Camera className="w-2.5 h-2.5 text-white" />
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarFile}
                      data-testid="profile-avatar-input"
                    />
                  </label>
                  <div className="flex-1 min-w-0">
                    <label className="block text-[11px] text-gray-400 uppercase tracking-wider mb-1">Nombre completo</label>
                    <input
                      data-testid="profile-name-input"
                      type="text"
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      placeholder="Tu nombre"
                      className="w-full px-3 py-2 rounded-lg bg-white/60 border border-gray-200/60 text-sm text-gray-900 outline-none focus:border-gray-300 placeholder:text-gray-400"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    data-testid="profile-save-btn"
                    onClick={handleSaveProfile}
                    disabled={saving || !draftName.trim()}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                      savedTick
                        ? 'bg-emerald-500 text-white'
                        : saving || !draftName.trim()
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]'
                    }`}
                  >
                    {savedTick ? <><Check className="w-3.5 h-3.5" /> Guardado</> : saving ? 'Guardando...' : 'Guardar'}
                  </button>
                  {!profileIncomplete && (
                    <button
                      onClick={() => { setEditing(false); }}
                      className="text-xs text-gray-400 hover:text-gray-700 transition-colors px-2.5"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Credits + Settings */}
          <section data-testid="credits-section" className="space-y-0.5">
            <button onClick={onShowCredits} data-testid="credits-item" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-black/[0.02] transition-colors text-left">
              <Zap className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-700 flex-1">Credits</span>
              <span className="text-xs text-emerald-500 font-semibold">{credits}</span>
            </button>
            <button onClick={onShowSettings} data-testid="settings-item" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-black/[0.02] transition-colors text-left">
              <Settings className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-700">Settings</span>
            </button>
            {isLoggedIn && (
              <button
                data-testid="signout-item"
                onClick={async () => {
                  await signOut();
                  try { localStorage.removeItem('ruido_project_references_v1'); } catch { /* noop */ }
                  navigate('/', { replace: true });
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-black/[0.02] transition-colors text-left"
              >
                <LogOut className="w-4 h-4 text-rose-400 shrink-0" />
                <span className="text-sm text-rose-500">Cerrar sesión</span>
              </button>
            )}
          </section>

          {/* Files — visual media archive */}
          <section data-testid="files-section">
            <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3 px-1">Files</h3>
            <MediaArchive files={allFiles} maxVisible={9} />
          </section>
        </div>
      </ScrollArea>
    </div>
  );
}
